import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const DiversityChart = ({ diversityData }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const chartHeight = 320;
  const outerRadius = isMobile ? 80 : 110;
  const innerRadius = isMobile ? 34 : 45;
  // Define colors for each demographic group
 const COLORS = {
  'African American': '#8884d8',   // purple-blue
  'Asian': '#82ca9d',              // green
  'Hispanic/Latino': '#E07A5F',     // coral
  'White': '#ff7c7c',               // soft red
  'Multiracial': '#6A4C93',         // deep purple (unique)
  'Native American': '#d084d0',     // lavender
  'Pacific Islander': '#ffb347',    // orange
  'International': '#5BC0EB',       // blue
  'Unknown': '#ddd',                // neutral gray
  'Other': '#ddd'                   // alias for Unknown
};


  // Convert diversity data to chart format and calculate percentages.
  // Values may arrive on EITHER scale depending on the dataset:
  //   - fractions 0-1   (synthetic_data_filled_updated_latest.csv)
  //   - percentages 0-100 (greatschools_45_plus_77.csv, incl. small real 0.2%)
  // Detect the scale per school from the row total and normalize to 0-100,
  // so fractions don't round to 0% and percentages don't blow up to 9000%.
  const rawEntries = Object.entries(diversityData || {})
    .map(([name, value]) => [name, parseFloat(value)])
    .filter(([, numeric]) => !Number.isNaN(numeric) && numeric > 0);

  const rowTotal = rawEntries.reduce((sum, [, numeric]) => sum + numeric, 0);
  // If the values sum to ~1 they are fractions -> scale up to percentages.
  const scale = rowTotal > 0 && rowTotal <= 1.5 ? 100 : 1;

  const chartData = rawEntries
    .map(([name, numeric]) => {
      const scaled = numeric * scale;
      const displayName = name === "Unknown" ? "Other" : name;
      return {
        name: displayName,
        value: scaled,
        percentage: Math.round(scaled)
      };
    })
    .sort((a, b) => b.value - a.value); // Sort by value descending

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{data.payload.name}</p>
          <p className="text-sm text-gray-600">
            {data.payload.percentage}% of students
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend
  const CustomLegend = ({ payload }) => {
    if (isMobile) return null;
    return (
      <div className="flex flex-wrap justify-center gap-3 mt-4 text-sm">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center text-sm">
            <div
              className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700">
              {entry.value} ({chartData.find((d) => d.name === entry.value)?.percentage}%)
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderMobileLegend = () => {
    if (!isMobile) return null;
    return (
      <div className="mt-6 grid grid-cols-2 gap-x-2 gap-y-2 text-[11px] leading-tight w-full">
        {chartData.map((entry, index) => (
          <div key={index} className="flex items-center space-x-2">
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: COLORS[entry.name] || '#cccccc' }}
            />
            <span className="text-gray-700">
              {entry.name} ({entry.percentage}%)
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-600 mb-2">No Diversity Data Available</h3>
        <p className="text-gray-500">Student demographic information is not available for this school.</p>
      </div>
    );
  }

  const chartContainerClass = isMobile
    ? "h-64 w-full relative flex justify-center items-center"
    : "";
  const chartContainerStyle = isMobile ? { maxWidth: 240 } : { height: chartHeight };

  const summaryClass = isMobile
    ? "mt-6 bg-gray-50 rounded-lg p-3 text-sm"
    : "mt-6 bg-gray-50 rounded-lg p-4";
  const summaryTitleClass = isMobile
    ? "font-medium text-gray-900 mb-2 text-sm"
    : "font-medium text-gray-900 mb-3";

  return (
    <div className={`w-full ${isMobile ? "flex flex-col" : ""}`}>
      <div
        style={chartContainerStyle}
        className={`${chartContainerClass} ${isMobile ? "mx-auto" : ""}`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              outerRadius={outerRadius}
              innerRadius={innerRadius}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={COLORS[entry.name] || '#cccccc'} 
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {!isMobile && <Legend content={<CustomLegend />} />}
          </PieChart>
        </ResponsiveContainer>
      </div>

      {renderMobileLegend()}
      
      {/* Summary statistics */}
      <div className={summaryClass}>
        <h4 className={summaryTitleClass}>Diversity Summary</h4>
        <div className={`grid grid-cols-2 gap-4 ${isMobile ? "text-sm" : "text-sm"}`}>
          <div>
            <span className="text-gray-600">Total Groups:</span>
            <span className="ml-2 font-medium">{chartData.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Largest Group:</span>
            <span className="ml-2 font-medium">
              {chartData[0]?.name} ({chartData[0]?.percentage}%)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiversityChart;
